import type {
  AccessRequest,
  AccessRequestOrigin,
} from "@/lib/api/access-requests";
import {
  formatAccessScopeLabel,
  getRequestIntegrationsDisplay,
} from "@/lib/api/access-request-form";
import type {
  DemoLocation,
  SandboxCredentialsStatus,
  SandboxWorkspace,
} from "@/lib/api/glofox/sandbox-workspaces";
import type { TestLocationProvisionStatus } from "@/lib/api/glofox/spin-up-test-location";

export type Environment = "Sandbox" | "Live";
export type RequestStatus = "Approved" | "Pending" | "Declined";

export const MAX_SANDBOX_TEST_LOCATIONS = 2;

const DEMO_LOCATION_NUMBER = / - demo - (\d+)$/;

/** UI fallback when session has no company_name; omit from demo location names. */
export const FALLBACK_BUSINESS_NAME = "Your business";

const MAX_DEMO_LOCATION_NAME_LENGTH = 100;
const MAX_GLOFOX_INTEGRATOR_NAME_LENGTH = 255;

/**
 * Optimistic display name matching backend `demoLocationDisplayName`:
 * `{company} - {integrator} - demo - {N}`, or `{integrator} - demo - {N}`
 * when company is empty / the UI fallback.
 */
export function demoLocationDisplayName(
  businessName: string,
  integratorName: string,
  locationNumber: number,
): string {
  const suffix = ` - demo - ${locationNumber}`;
  const company = businessName.trim();
  const integrator = integratorName.trim();
  const prefix =
    company !== "" && company !== FALLBACK_BUSINESS_NAME
      ? `${company} - ${integrator}`
      : integrator;
  const maxPrefixLength = MAX_DEMO_LOCATION_NAME_LENGTH - [...suffix].length;
  const truncated =
    [...prefix].length > maxPrefixLength
      ? [...prefix].slice(0, maxPrefixLength).join("")
      : prefix;
  return `${truncated}${suffix}`;
}

/**
 * Preview of the Glofox integrator name (AES-373), matching backend
 * `glofoxIntegratorName`: `{company} - {integrator} - {Sandbox|Live}`.
 */
export function glofoxIntegratorDisplayName(
  businessName: string,
  integratorName: string,
  environment: "sandbox" | "live" | Environment,
): string {
  const envLabel =
    environment === "live" || environment === "Live" ? "Live" : "Sandbox";
  const suffix = ` - ${envLabel}`;
  const company = businessName.trim();
  const integrator = integratorName.trim() || "…";
  const prefix =
    company !== "" && company !== FALLBACK_BUSINESS_NAME
      ? `${company} - ${integrator}`
      : integrator;
  const maxPrefixLength = MAX_GLOFOX_INTEGRATOR_NAME_LENGTH - [...suffix].length;
  const truncated =
    [...prefix].length > maxPrefixLength
      ? [...prefix].slice(0, maxPrefixLength).join("")
      : prefix;
  return `${truncated}${suffix}`;
}

export interface TestLocation {
  id: string;
  name: string;
  status: TestLocationProvisionStatus;
  /** Glofox location id. Present after provisioning succeeds. */
  branchId?: string;
  errorMessage?: string;
}

export interface IntegratorRequest {
  id: string;
  name: string;
  business: string;
  environment: Environment;
  status: RequestStatus;
  requested: string;
  scope: string;
  useCase: string;
  declineReason?: string;
  /** True only when status is Declined and the applicant may contest once. */
  canContest?: boolean;
  /** Present after a successful contest (request reopened as Pending). */
  contestedAt?: string | null;
  /**
   * Source API row for contest form prefills. Present for server-backed rows.
   */
  sourceRequest?: AccessRequest;
  /**
   * How the request was created (AES-359+). Used for credentials unlock, not UI.
   */
  origin?: AccessRequestOrigin | null;
  /**
   * Existing Glofox integrator id from the access request or sandbox workspace
   * (AES-359+). Unlocks credentials when set even with zero demo locations.
   */
  integratorId?: string | null;
  /** True after the first API credential request for this integrator. */
  credentialsRequested?: boolean;
  /** Workspace credentials_status from the API (`none` is omitted). */
  credentialsStatus?: SandboxCredentialsStatus;
  /** Sandbox workspace id from AES-278 (needed for credentials POST). */
  sandboxWorkspaceId?: string;
  locations: TestLocation[];
  scopeChangePending?: boolean;
  activity: Array<{ label: string; date: string }>;
}

/** True when the access request was imported from an existing Glofox integrator. */
export function isImportedFromGlofox(
  origin?: AccessRequestOrigin | null,
): boolean {
  return origin === "imported_from_glofox";
}

function trimOptionalId(value?: string | null): string | undefined {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
}

/**
 * True when sandbox credentials may be requested without a ready demo location
 * (imported row or an integrator id already held on the request/workspace).
 */
export function hasHeldIntegrator(request: {
  origin?: AccessRequestOrigin | null;
  integratorId?: string | null;
}): boolean {
  return (
    Boolean(trimOptionalId(request.integratorId)) ||
    isImportedFromGlofox(request.origin)
  );
}

/**
 * Credentials CTA unlock: at least one ready test location, or (when
 * `api-portal-integrator-sync` is on) a held integrator id / imported origin.
 */
export function canRequestSandboxCredentials(
  request: IntegratorRequest,
  options: { integratorSyncEnabled?: boolean } = {},
): boolean {
  if (request.locations.some((location) => location.status === "success")) {
    return true;
  }
  if (!options.integratorSyncEnabled) {
    return false;
  }
  return hasHeldIntegrator(request);
}

export function formatPortalRequestDate(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return iso;
  }
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function mapStatus(status: AccessRequest["status"]): RequestStatus {
  switch (status) {
    case "approved":
      return "Approved";
    case "rejected":
      return "Declined";
    default:
      return "Pending";
  }
}

function formatScope(request: AccessRequest): string {
  if (request.access_scope === "namespaces" && request.namespaces.length > 0) {
    const label =
      request.namespaces.length === 1 ? "Namespace" : "Namespaces";
    return `${label}: ${request.namespaces.join(", ")}`;
  }
  if (request.access_scope === "locations") {
    const names = request.location_names ?? [];
    if (names.length > 0) {
      const label = names.length === 1 ? "Location" : "Locations";
      return `${label}: ${names.join(", ")}`;
    }
    if (request.location_ids.length > 0) {
      const label =
        request.location_ids.length === 1 ? "Location ID" : "Location IDs";
      return `${label}: ${request.location_ids.join(", ")}`;
    }
  }
  if (request.access_scope) {
    return formatAccessScopeLabel(request.access_scope);
  }
  return request.environment === "sandbox" ? "Not required" : "Not provided";
}

function buildDisplayName(request: AccessRequest): string {
  const integratorName = request.integrator_name?.trim();
  if (integratorName) {
    return integratorName;
  }

  const integrations = getRequestIntegrationsDisplay(request).join(" + ");
  const useCase = request.intended_use_case.trim();
  if (useCase.length > 0) {
    const shortened =
      useCase.length > 48 ? `${useCase.slice(0, 45).trimEnd()}…` : useCase;
    return shortened;
  }
  return `${integrations} access`;
}

const AWAITING_REVIEW_LABEL = "Awaiting API Activation review";
const STILL_PROVISIONING_LABEL = "Test location still provisioning";

const PERSISTED_ACTIVITY_LABELS: Record<string, string> = {
  request_submitted: "Request submitted",
  request_approved: "Integrator approved",
  request_declined: "Request declined",
  request_contested: "Request contested",
  test_location_provisioning_started: "Test location provisioning started",
  test_location_created: "Test location created",
  test_location_provisioning_failed: "Test location provisioning failed",
  credentials_requested: "API credential request submitted",
  credentials_resent: "API credentials resent",
};

function mapPersistedActivity(
  events: AccessRequest["activity"],
): IntegratorRequest["activity"] {
  if (!events?.length) {
    return [];
  }
  const mapped: IntegratorRequest["activity"] = [];
  for (const event of events) {
    const label = PERSISTED_ACTIVITY_LABELS[event.type];
    if (!label) {
      continue;
    }
    mapped.push({
      label,
      date: formatPortalRequestDate(event.created_at),
    });
  }
  return mapped;
}

function withDerivedActivity(
  activity: IntegratorRequest["activity"],
  status: RequestStatus,
  requested: string,
  locations: TestLocation[],
): IntegratorRequest["activity"] {
  let next = activity;
  if (
    status === "Pending" &&
    next[0]?.label !== AWAITING_REVIEW_LABEL
  ) {
    next = [{ label: AWAITING_REVIEW_LABEL, date: requested }, ...next];
  }
  if (
    locations.some((location) => location.status === "pending") &&
    next[0]?.label !== STILL_PROVISIONING_LABEL
  ) {
    next = [{ label: STILL_PROVISIONING_LABEL, date: requested }, ...next];
  }
  return next;
}

function buildActivity(
  status: RequestStatus,
  requested: string,
  updated: string,
): IntegratorRequest["activity"] {
  if (status === "Approved") {
    return [
      { label: "Integrator approved", date: updated },
      { label: "Request submitted", date: requested },
    ];
  }
  if (status === "Declined") {
    return [
      { label: "Request declined", date: updated },
      { label: "Request submitted", date: requested },
    ];
  }
  return [
    { label: AWAITING_REVIEW_LABEL, date: requested },
    { label: "Request submitted", date: requested },
  ];
}

function demoLocationBranchId(location: DemoLocation): string | undefined {
  const branchId = location.branch_id?.trim();
  return branchId ? branchId : undefined;
}

function withBranchId(
  location: Omit<TestLocation, "branchId">,
  branchId?: string,
): TestLocation {
  return branchId ? { ...location, branchId } : location;
}

/** Maps a sandbox demo location into the Dev Portal test-location row. */
export function mapDemoLocationToTestLocation(
  location: DemoLocation,
): TestLocation {
  const branchId = demoLocationBranchId(location);
  if (location.status === "ready") {
    return withBranchId(
      {
        id: location.id,
        name: location.display_name,
        status: "success",
      },
      branchId,
    );
  }
  if (location.status === "creating") {
    return withBranchId(
      {
        id: location.id,
        name: location.display_name,
        status: "pending",
      },
      branchId,
    );
  }
  return withBranchId(
    {
      id: location.id,
      name: location.display_name,
      status: "error",
      errorMessage:
        location.failure_reason?.trim() ||
        "We could not create this test location. Try again or contact support.",
    },
    branchId,
  );
}

export type CredentialsActionKind = "request" | "resend" | "retry";

/** Button copy kind from persisted credentials_status. Failed is retry, not resend. */
export function credentialsActionKind(
  status?: SandboxCredentialsStatus,
): CredentialsActionKind {
  if (status === "failed") {
    return "retry";
  }
  if (status === "issued" || status === "pending") {
    return "resend";
  }
  return "request";
}

export function credentialsActionLabels(status?: SandboxCredentialsStatus): {
  title: string;
  action: string;
} {
  switch (credentialsActionKind(status)) {
    case "retry":
      return {
        title: "Retry API credentials",
        action: "Retry credentials",
      };
    case "resend":
      return {
        title: "Resend API credentials",
        action: "Resend credentials",
      };
    default:
      return {
        title: "Request API credentials",
        action: "Request credentials",
      };
  }
}

/** True when the workspace has already started or finished a credentials request. */
export function credentialsStatusMeansRequested(
  status?: SandboxCredentialsStatus,
): boolean {
  return credentialsActionKind(status) !== "request";
}

function coalesceCredentialsStatus(
  preferred?: SandboxCredentialsStatus,
  fallback?: SandboxCredentialsStatus,
): SandboxCredentialsStatus | undefined {
  if (preferred && preferred !== "none") {
    return preferred;
  }
  if (fallback && fallback !== "none") {
    return fallback;
  }
  return undefined;
}

/**
 * Attaches sandbox workspace id, credentials state, and demo locations onto
 * matching access-request rows (by `access_request_id`).
 */
export function attachSandboxLocationsToRequests(
  requests: IntegratorRequest[],
  workspaces: SandboxWorkspace[],
): IntegratorRequest[] {
  if (workspaces.length === 0) {
    return requests;
  }

  const workspaceByAccessRequestId = new Map<
    string,
    {
      workspaceId: string;
      locations: TestLocation[];
      credentialsStatus?: SandboxCredentialsStatus;
      integratorId?: string;
    }
  >();
  for (const workspace of workspaces) {
    const mapped = workspace.locations.map(mapDemoLocationToTestLocation);
    const existing = workspaceByAccessRequestId.get(workspace.access_request_id);
    const credentialsStatus = coalesceCredentialsStatus(
      workspace.credentials_status,
      existing?.credentialsStatus,
    );
    const integratorId =
      trimOptionalId(workspace.integrator_id) ?? existing?.integratorId;
    workspaceByAccessRequestId.set(workspace.access_request_id, {
      workspaceId: workspace.id,
      locations: existing ? [...existing.locations, ...mapped] : mapped,
      credentialsStatus,
      integratorId,
    });
  }

  return requests.map((request) => {
    const match = workspaceByAccessRequestId.get(request.id);
    if (!match) {
      return request;
    }
    const credentialsStatus = coalesceCredentialsStatus(
      match.credentialsStatus,
      request.credentialsStatus,
    );
    const integratorId =
      trimOptionalId(request.integratorId) ?? match.integratorId;
    return {
      ...request,
      sandboxWorkspaceId: match.workspaceId,
      integratorId,
      locations: match.locations.length > 0 ? match.locations : request.locations,
      credentialsStatus,
      credentialsRequested: credentialsStatusMeansRequested(credentialsStatus)
        ? true
        : request.credentialsRequested,
      activity: withDerivedActivity(
        request.activity,
        request.status,
        request.requested,
        match.locations.length > 0 ? match.locations : request.locations,
      ),
    };
  });
}

/**
 * Counts the sandbox test locations that hold a create slot for the signed-in
 * user, across every sandbox request. Ready and provisioning locations hold a
 * slot; failed attempts do not, so the user can retry.
 */
export function countActiveSandboxLocations(
  requests: IntegratorRequest[],
): number {
  return requests.reduce((total, request) => {
    if (request.environment !== "Sandbox") {
      return total;
    }
    const active = request.locations.filter(isActiveTestLocation).length;
    return total + active;
  }, 0);
}

function isActiveTestLocation(location: TestLocation): boolean {
  return location.status === "success" || location.status === "pending";
}

function parsedDemoLocationNumber(name: string): number {
  const match = name.match(DEMO_LOCATION_NUMBER);
  if (!match) {
    return 0;
  }
  const n = Number(match[1]);
  return Number.isInteger(n) && n >= 1 ? n : 0;
}

/**
 * Lowest free ` - demo - N` slot among creating/ready locations. Failed rows
 * do not occupy a number, so a retry reuses that slot instead of colliding
 * with an active location that already holds a higher number.
 */
export function nextSandboxLocationNumber(
  locations: TestLocation[],
  max = MAX_SANDBOX_TEST_LOCATIONS,
): number {
  const occupied = Array.from({ length: max + 1 }, () => false);
  let implicit = 0;
  for (const location of locations) {
    if (!isActiveTestLocation(location)) {
      continue;
    }
    const n = parsedDemoLocationNumber(location.name);
    if (n >= 1 && n <= max && !occupied[n]) {
      occupied[n] = true;
      continue;
    }
    implicit += 1;
  }
  for (let n = 1; n <= max; n += 1) {
    if (occupied[n]) {
      continue;
    }
    if (implicit > 0) {
      occupied[n] = true;
      implicit -= 1;
      continue;
    }
    return n;
  }
  return max + 1;
}

/** Maps a backend access request into the Dev Portal list row model. */
export function mapAccessRequestToIntegratorRequest(
  request: AccessRequest,
  businessName: string,
): IntegratorRequest {
  const status = mapStatus(request.status);
  const requested = formatPortalRequestDate(request.created_at);
  const updated = formatPortalRequestDate(request.updated_at);

  const persisted = mapPersistedActivity(request.activity);
  const activity =
    persisted.length > 0
      ? withDerivedActivity(persisted, status, requested, [])
      : buildActivity(status, requested, updated);

  return {
    id: request.id,
    name: buildDisplayName(request),
    business: businessName,
    environment: request.environment === "sandbox" ? "Sandbox" : "Live",
    status,
    requested,
    scope: formatScope(request),
    useCase: request.intended_use_case,
    declineReason: request.rejection_reason?.trim() || undefined,
    canContest: request.can_contest === true,
    contestedAt: request.contested_at ?? null,
    sourceRequest: request,
    origin: request.origin ?? null,
    integratorId: trimOptionalId(request.integrator_id) ?? null,
    locations: [],
    activity,
  };
}

/**
 * Merges API-hydrated locations with in-flight optimistic rows (pending ids
 * not yet present on the server).
 */
export function mergeTestLocations(
  fromApi: TestLocation[],
  fromClient: TestLocation[],
): TestLocation[] {
  const apiIds = new Set(fromApi.map((location) => location.id));
  const pendingOnly = fromClient.filter(
    (location) => location.status === "pending" && !apiIds.has(location.id),
  );
  return [...fromApi, ...pendingOnly];
}

/**
 * Keeps in-flight client sandbox state (optimistic pending locations,
 * credential resend flag) when a list reload replaces rows. Server-hydrated
 * locations win; pending-only client rows are appended. Request activity comes
 * from the list payload, not from previous client-only rows.
 */
export function preserveClientPortalState(
  nextRequests: IntegratorRequest[],
  previousRequests: IntegratorRequest[],
): IntegratorRequest[] {
  if (previousRequests.length === 0) {
    return nextRequests;
  }

  const previousById = new Map(
    previousRequests.map((request) => [request.id, request] as const),
  );

  return nextRequests.map((request) => {
    const previous = previousById.get(request.id);
    if (!previous) {
      return request;
    }

    const locations = mergeTestLocations(request.locations, previous.locations);
    const sandboxWorkspaceId =
      request.sandboxWorkspaceId ?? previous.sandboxWorkspaceId;
    const integratorId =
      trimOptionalId(request.integratorId) ??
      trimOptionalId(previous.integratorId) ??
      null;
    const credentialsStatus = coalesceCredentialsStatus(
      request.credentialsStatus,
      previous.credentialsStatus,
    );
    let credentialsRequested = request.credentialsRequested;
    if (credentialsStatusMeansRequested(credentialsStatus)) {
      credentialsRequested = true;
    } else if (previous.credentialsRequested === true) {
      credentialsRequested = true;
    }
    const locationsChanged =
      locations.length !== request.locations.length ||
      locations.some((location, index) => location !== request.locations[index]);

    if (
      !locationsChanged &&
      credentialsStatus === request.credentialsStatus &&
      credentialsRequested === request.credentialsRequested &&
      sandboxWorkspaceId === request.sandboxWorkspaceId &&
      integratorId === (request.integratorId ?? null)
    ) {
      return request;
    }

    return {
      ...request,
      locations,
      sandboxWorkspaceId,
      integratorId,
      credentialsStatus,
      credentialsRequested,
    };
  });
}

export type LocationProvisionResult = {
  id: string;
  name: string;
  status: TestLocationProvisionStatus;
  branchId?: string;
  errorMessage?: string;
};

function locationProvisionActivityLabel(
  status: TestLocationProvisionStatus,
): string {
  if (status === "success") {
    return "Test location created";
  }
  if (status === "pending") {
    return "Test location still provisioning";
  }
  return "Test location provisioning failed";
}

/**
 * Applies a spin-up result onto a request. If the optimistic pending row was
 * wiped by a list reload, appends the location instead. Pending results keep
 * the row blocked for another create.
 */
export function applyLocationProvisionResult(
  requests: IntegratorRequest[],
  requestId: string,
  pendingId: string,
  result: LocationProvisionResult,
): IntegratorRequest[] {
  const index = requests.findIndex((request) => request.id === requestId);
  if (index < 0) {
    return requests;
  }

  const request = requests[index];
  if (!request) {
    return requests;
  }

  const finished: TestLocation = result.branchId
    ? {
        id: result.id,
        name: result.name,
        status: result.status,
        branchId: result.branchId,
        errorMessage: result.errorMessage,
      }
    : {
        id: result.id,
        name: result.name,
        status: result.status,
        errorMessage: result.errorMessage,
      };
  const pendingIndex = request.locations.findIndex(
    (location) => location.id === pendingId,
  );
  const locations =
    pendingIndex >= 0
      ? request.locations.map((location, locationIndex) =>
          locationIndex === pendingIndex ? finished : location,
        )
      : [...request.locations, finished];

  const next = requests.slice();
  next[index] = {
    ...request,
    locations,
    activity: [
      {
        label: locationProvisionActivityLabel(result.status),
        date: formatPortalRequestDate(new Date().toISOString()),
      },
      ...request.activity,
    ],
  };
  return next;
}
